/**
 * In-process fetch wrapper for the tenant LLM proxy.
 *
 * Replaces the previous service-worker interception layer. The SW
 * approach worked when it worked, but Chrome's SW lifecycle on
 * shift-force-reloaded clients (`Cmd+Shift+R`), background→foreground
 * transitions, and CDN-fronted hosts meant `navigator.serviceWorker
 * .controller` could be null even with an activated registration in
 * scope — and during those windows outbound LLM fetches slipped past
 * the SW and hit upstream providers directly with the Origin JWT as
 * the bearer token. Way too much complexity for what the SW actually
 * does (URL + auth-header rewrite), so we moved it into the page.
 *
 * How it works:
 *
 *   - At app boot, `installLlmProxyShim()` snapshots the original
 *     `globalThis.fetch` and replaces it with `proxiedFetch`.
 *   - On every fetch, the wrapper inspects the destination hostname.
 *     If it's a known LLM provider host (`api.anthropic.com`,
 *     `openrouter.ai`, `api.openai.com`) the wrapper redirects the
 *     request to `${API_BASE_URL}/v1/tenant/llm-proxy`, normalizes
 *     the bearer token onto `Authorization: Bearer <jwt>`, and
 *     whitelists the safe forward headers (content-type / accept /
 *     anthropic-version). Everything else passes through untouched.
 *   - The page is the only execution context, so there's no
 *     controller binding, no lifecycle race, no force-reload edge
 *     case, no claim handshake. The wrapper is in place from the
 *     first user-triggered fetch onward.
 *
 * We unregister any stale `llm-proxy-sw.js` registration on install
 * so the deprecated SW stops intercepting on browsers that picked it
 * up before this change landed.
 *
 * Why the OpenAI/Anthropic SDKs don't expose a fetch hook — pi-ai
 * constructs them inline (`new OpenAI({ apiKey, baseURL, ... })`) and
 * doesn't forward a fetch option, so we don't have an SDK-level
 * injection point. Patching `globalThis.fetch` is the next best
 * thing: the Stainless SDKs read `globalThis.fetch` at request time
 * (not construction), so a wrapper installed before any chat send
 * intercepts cleanly.
 */

import { API_BASE_URL } from "./featureFlags.js";
import { ensureFreshAccessToken } from "./authBridge.js";
import { decodeChatCredential, CHAT_ID_HEADER, CHAT_ROLE_HEADER } from "./chatCredential.js";

const LLM_HOSTS = new Set([
  "api.anthropic.com",
  "openrouter.ai",
  "api.openai.com",
]);

const PROXY_URL = `${API_BASE_URL.replace(/\/+$/, "")}/v1/tenant/llm-proxy`;

// The tenant's custom (BYO-gateway) provider host, registered at chat start
// by `buildAgentRuntime` when the provider is "custom". The shim routes
// fetches to this host through the backend proxy exactly like the hardcoded
// LLM hosts above, so the custom provider key never leaves the server.
// `null` when the tenant isn't on a custom provider.
let customHost = null;

/**
 * Register (or clear) the tenant's custom LLM host. Pass the hostname only
 * (e.g. "gateway.example.com"); a falsy value clears it. Idempotent — safe
 * to call on every chat start.
 */
export function setCustomLlmHost(host) {
  customHost = host && String(host).trim() ? String(host).trim().toLowerCase() : null;
}

let installed = false;
let origFetch = null;

/**
 * Idempotent install. Call once at app boot. Returns nothing — the
 * wrapper is in place synchronously, so chat code can assume future
 * LLM fetches are intercepted from this point on. The stale-SW
 * cleanup is fired in the background and never blocks the caller.
 */
export function installLlmProxyShim() {
  if (installed) return;
  if (typeof globalThis === "undefined" || typeof globalThis.fetch !== "function") return;

  // Clear out any service worker left over from the previous shim.
  // We don't await this — it's purely cleanup; the wrapper below
  // takes effect immediately regardless. Without this the old SW
  // would keep intercepting LLM fetches on browsers that picked it
  // up before we migrated, which mostly still works but defeats the
  // point of moving in-process.
  unregisterLegacySw();

  installed = true;
  origFetch = globalThis.fetch.bind(globalThis);
  globalThis.fetch = proxiedFetch;
}

async function unregisterLegacySw() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    for (const r of regs) {
      const url = r.active?.scriptURL
        || r.installing?.scriptURL
        || r.waiting?.scriptURL
        || "";
      if (url.includes("llm-proxy-sw.js")) await r.unregister();
    }
  } catch (err) {
    console.warn("[llm-proxy] legacy SW unregister failed:", err);
  }
}

function extractUrl(input) {
  let raw;
  if (typeof input === "string") raw = input;
  else if (typeof URL !== "undefined" && input instanceof URL) raw = input.href;
  else if (input && typeof input.url === "string") raw = input.url;
  else return null;
  try {
    return new URL(raw, typeof location !== "undefined" ? location.href : undefined);
  } catch {
    return null;
  }
}

// Build the forward headers for a proxied LLM request from the SDK's source
// headers. Extracted + exported so the credential-split — the load-bearing bit
// — is unit-testable without a full fetch. The bearer pi-ai handed us (from
// `Authorization: Bearer` or `x-api-key`) may be a COMPOSITE carrying the chat
// id + agent role (see chatCredential.js); split it back out so the clean Origin
// JWT rides `Authorization` and the chat context becomes the proxy's audit
// headers. A plain (non-composite) bearer decodes to itself with null chat
// context, so nothing changes when chat persistence is off. Only a small
// whitelist of source headers is forwarded — anything else the SDKs set
// (x-stainless-*, direct-browser-access, …) is irrelevant to the proxy and only
// invites preflight churn.
export function buildProxyHeaders(srcHeaders) {
  let bearer = null;
  const auth = srcHeaders.get("Authorization");
  if (auth && auth.toLowerCase().startsWith("bearer ")) {
    bearer = auth.slice("bearer ".length).trim();
  } else if (auth) {
    bearer = auth.trim();
  }
  const xApiKey = srcHeaders.get("x-api-key");
  if (!bearer && xApiKey) bearer = xApiKey.trim();

  const { chatId, role, jwt } = decodeChatCredential(bearer);

  const outHeaders = new Headers();
  for (const name of ["content-type", "accept", "anthropic-version"]) {
    const v = srcHeaders.get(name);
    if (v) outHeaders.set(name, v);
  }
  if (jwt) outHeaders.set("Authorization", `Bearer ${jwt}`);
  if (chatId) outHeaders.set(CHAT_ID_HEADER, chatId);
  if (role) outHeaders.set(CHAT_ROLE_HEADER, role);
  return outHeaders;
}

async function proxiedFetch(input, init) {
  const url = extractUrl(input);
  // Intercept the hardcoded provider hosts plus, when configured, the
  // tenant's custom gateway host. Everything else passes through untouched.
  const isProxiedHost =
    !!url &&
    (LLM_HOSTS.has(url.hostname) ||
      // The custom gateway is matched on host INCLUDING port (customHost is
      // stored as `new URL(baseUrl).host`), so a gateway on a non-default port
      // like :8443 is still intercepted — otherwise the SDK would dial it
      // directly and leak the Origin JWT past the shim.
      (customHost && url.host.toLowerCase() === customHost));
  if (!isProxiedHost) return origFetch(input, init);

  const isRequest = typeof Request !== "undefined" && input instanceof Request;
  const method = init?.method
    || (isRequest ? input.method : undefined)
    || "GET";

  // Source headers: Request-object headers first (lowest priority),
  // then `init.headers` on top, matching what the browser would
  // resolve if we were calling the underlying fetch ourselves.
  const srcHeaders = new Headers();
  if (isRequest) {
    for (const [k, v] of input.headers.entries()) srcHeaders.set(k, v);
  }
  if (init?.headers) {
    const h = new Headers(init.headers);
    for (const [k, v] of h.entries()) srcHeaders.set(k, v);
  }

  // Extract whatever auth credential the SDK set. OpenAI / OpenRouter
  // use `Authorization: Bearer <key>`; Anthropic uses `x-api-key`. We
  // normalize both to a single `Authorization: Bearer <jwt>` for the
  // proxy. The proxy strips it server-side and injects the real
  // upstream provider key before forwarding.
  const outHeaders = buildProxyHeaders(srcHeaders);

  // Body. For Request objects we have to clone before reading because
  // the browser will reuse the original elsewhere. For init.body
  // pass-through, the SDKs hand us a string and we forward as-is.
  let body;
  if (method === "GET" || method === "HEAD") {
    body = undefined;
  } else if (init && "body" in init) {
    body = init.body;
  } else if (isRequest) {
    body = await input.clone().arrayBuffer();
  }

  // The auth shim downstream stamps whatever access token is in the
  // store onto this request — make sure that token isn't expired first.
  // Chat sessions routinely idle past the 5-minute token lifetime with
  // no other /v1/* traffic to trigger a 401-driven refresh, and a stale
  // token used to die silently (O-2046). Failure here is non-fatal: the
  // request proceeds and the proxy's 401 surfaces through the SDK.
  await ensureFreshAccessToken().catch(() => {});

  return origFetch(PROXY_URL, {
    method,
    headers: outHeaders,
    body,
    redirect: "follow",
    credentials: "omit",
    signal: init?.signal ?? (isRequest ? input.signal : undefined),
  });
}
